import { z } from "zod";
import { captureWorkspaceRequest, payloadHeaders, workspaceFetch, workspaceStream } from "@/lib/workspace-lifetime";
import { workspaceHeaders, type WorkspaceIdentity } from "@/lib/workspace-api";
import { parsePipelineStepsResponse } from "@/lib/pipeline-trace";
import { SessionExpiredError, type SessionStatus } from "@/lib/session-activity";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "/api/v1";

// ===== Types =====
export interface ApiResponse<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
  error?: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: string;
  email_verified: boolean;
  phone_verified: boolean;
  two_factor_enabled: boolean;
  created_at: string;
  // Phase 5/6 of [[rbac-permission-overhaul-initiative]]: only /profile/me
  // populates these (see UserReadDto's backend docstring) — every other
  // route returning a User-shaped object (register/update-profile/etc.)
  // leaves both undefined, so they're optional here, not just on the wire.
  account_status?: "active" | "suspended" | "banned";
  capabilities?: string[];
}

export interface LoginResponse {
  // Tokens no longer travel in the body (Phase 2 — HttpOnly cookies instead).
  // `authenticated` distinguishes a completed login from a 2FA-required one.
  authenticated?: boolean;
  requires_2fa?: boolean;
  temp_token?: string;
  message?: string;
}

export interface PipelineRun {
  id: string;
  job_id: string;
  query: string;
  user_query?: string;
  status: "pending" | "running" | "in_progress" | "paused" | "completed" | "failed" | "cancelled";
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  is_secure: boolean;
  is_vault?: boolean;
  attachments?: Array<{ file_name?: string; [key: string]: unknown }>;
  total_cost_usd?: number;
  total_tokens?: number;
  output?: Record<string, unknown>;
  summary?: string;
  error_message?: string | null;
  domain?: string;
  execution_plan?: string | Record<string, unknown>;
  execution_plan_sections?: Record<string, unknown>;
  final_result?: Record<string, unknown>;
  extraction_summary?: Record<string, unknown>;
  validation_summary?: Record<string, unknown>;
  compliance_summary?: Record<string, unknown>;
  report_summary?: Record<string, unknown>;
  postprocessing?: {
    status: "waiting_for_result" | "pending" | "running" | "completed" | "failed" | "not_applicable";
    total: number;
    pending: number;
    in_progress: number;
    done: number;
    failed: number;
    cancelled: number;
  };
  feedback?: {
    is_helpful: boolean;
    feedback_text?: string;
    created_at?: string;
  };
}

export interface PipelineStep {
  id: string;
  step_id: string;
  logical_step_id: string;
  title: string;
  status: string;
  agent_type: string;
  input?: any;
  result?: any;
  started_at: string;
  completed_at: string;
  total_tokens?: number;
  total_cost_usd?: number;
  retry_count: number;
}

export interface AnalyticsData {
  total_users: number;
  total_runs: number;
  secured_runs: number;
  non_secured_runs: number;
  status_distribution: Record<string, number>;
  avg_processing_time: number;
  total_volume_bytes: number;
  daily_trends: Array<{ date: string; completed: number; failed: number; total: number }>;
  type_breakdown: Record<string, number>;
  domain_distribution: Record<string, number>;
  avg_steps: number;
  avg_tokens_per_run: number;
  total_projected_cost: number;
  token_trends: Array<{ date: string; tokens: number }>;
  cost_trends: Array<{ date: string; cost: number }>;
  agent_usage_breakdown: Record<string, { tokens: number; cost: number }>;
  recent_activity: Array<{ job_id: string; query: string; status: string; created_at: string }>;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at?: string;
}

export interface Session {
  id: string;
  device_info?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  is_active: boolean;
  last_activity: string;
  created_at: string;
  expires_at: string;
}

export interface ActivityLog {
  id: string;
  action: string;
  resource?: string | null;
  details?: string | null;
  ip_address?: string | null;
  status: string;
  created_at: string;
}

export interface ApiRequestLog {
  id: string;
  endpoint?: string | null;
  path?: string | null;
  method?: string | null;
  status_code?: number | null;
  error_message?: string | null;
  client_ip?: string | null;
  user_agent?: string | null;
  request_headers?: Record<string, unknown> | null;
  request_params?: Record<string, unknown> | null;
  model_name?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  cost_usd?: number | null;
  latency_ms?: number | null;
  created_at: string;
}

export interface PromptTemplate {
  id: number;
  name: string;
  type: string;
  content: string;
  version: number;
  is_active: boolean;
}

export interface WorkflowConfig {
  id: number;
  name: string;
  config: Record<string, any>;
  version: number;
  is_active: boolean;
}

export interface DocumentKeywordNested {
  id: string;
  keyword: string;
  document_type: string;
}

export interface SubDomainNested {
  id?: string;
  subdomain_name: string;
  subdomain_title?: string;
  domain_title?: string;
  is_active?: boolean;
  prompt_ids?: number[];
  config_ids?: number[];
  prompt_templates?: PromptTemplate[];
  workflow_configs?: WorkflowConfig[];
  document_keywords?: DocumentKeywordNested[];
}

export interface Domain {
  tenant_id?: string | null;
  owner_user_id?: string | null;
  publication_state?: string | null;
  id: string;
  domain_name: string;
  domain_title?: string;
  domain?: Record<string, any>;
  is_active: boolean;
  versions: number;
  user_id?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  prompt_templates?: PromptTemplate[];
  workflow_configs?: WorkflowConfig[];
  sub_domains?: SubDomainNested[];
  document_keywords?: DocumentKeywordNested[];
}

export interface MainDomain {
  id: string;
  name: string;
  domain_id?: string | null;
  sub_domain_id?: string | null;
  created_at: string;
  updated_at: string;
  domain?: Domain | null;
  sub_domain?: SubDomainNested | null;
}

// ===== Session cookies (Phase 2 — see [[rbac-permission-overhaul-initiative]]) =====
// access_token/refresh_token are now HttpOnly cookies the browser attaches
// automatically (credentials: "include") — no JS ever reads or stores them,
// which is the entire point. Only the non-secret csrf_token cookie and the
// cached-user cache (non-sensitive profile display data, not a credential)
// are still touched from here.
const USER_KEY = "digi_user";
const CSRF_COOKIE_NAME = "csrf_token";

function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export function clearCachedUser(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(USER_KEY);
}

export function getCachedUser(): User | null {
  if (typeof window === "undefined") return null;
  try {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

export function setCachedUser(user: User): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// ===== Token Refresh =====
// The refresh_token cookie rides automatically; there's no access_token in
// the body to extract anymore. accountInactive is surfaced (rather than
// collapsing everything to a bare boolean) so a ban discovered during
// refresh doesn't get flattened into the same generic "session expired"
// message a plain expired/absent refresh token gets.
let isRefreshing = false;
let refreshPromise: Promise<{ ok: boolean; accountInactive?: boolean }> | null = null;

async function doRefresh(): Promise<{ ok: boolean; accountInactive?: boolean }> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfToken() ? { "X-CSRF-Token": getCsrfToken()! } : undefined,
    });
    if (res.ok) return { ok: true };
    if (res.status === 403) {
      const data = await res.json().catch(() => ({} as Record<string, unknown>));
      const inactive =
        (data as any)?.error?.code === "ACCOUNT_INACTIVE" ||
        res.headers.get("X-Account-Status") === "inactive";
      return { ok: false, accountInactive: inactive };
    }
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

async function refreshToken(): Promise<{ ok: boolean; accountInactive?: boolean }> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = doRefresh();

  try {
    return await refreshPromise;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

async function forceLoginRedirect(): Promise<void> {
  // An automatic failure clears this view only: another tab may have won
  // a concurrent refresh for the same stable session. Explicit user logout
  // remains the operation that revokes the server session.
  clearCachedUser();
  if (typeof window !== "undefined") {
    window.location.href = "/?login_required=1";
  }
}

export type AuthPolicy = "required" | "optional" | "none";

export interface ApiFetchOptions extends RequestInit {
  /**
   * required (default): on 401, refresh once and retry; on continued
   * failure or a 403 ACCOUNT_INACTIVE, clear local identity + redirect to login.
   * Use for ordinary protected/mutating calls.
   *
   * optional: on 401, still attempt one refresh (a valid refresh cookie may
   * exist even though this particular call 401'd), but never redirect on
   * failure — just return the failed response so the caller can treat it
   * as "not logged in." Use for the initial session probe on mount, where
   * a 401 is the expected, ordinary state for a not-yet-logged-in visitor,
   * not an error — redirecting here is what caused every anonymous page
   * load to bounce through "/?login_required=1".
   *
   * none: never attempt a refresh, never redirect — just hand back
   * whatever the server actually said. Use for login/register/OTP/2FA/
   * password-reset calls: a 401/403 there is real signal (wrong password,
   * expired code, banned account) the caller must display verbatim, not
   * something this wrapper should intercept and overwrite with a generic
   * "Session expired" while also navigating the user away mid-form.
   */
  authPolicy?: AuthPolicy;
}

// ===== Base Fetch =====
export async function apiFetch<T = unknown>(
  endpoint: string,
  options: ApiFetchOptions = {}
): Promise<ApiResponse<T>> {
  const { authPolicy = "required", ...fetchOptions } = options;
  const lifetime = authPolicy === "none" || endpoint.startsWith("/auth/") || endpoint.startsWith("/profile/me") || endpoint.startsWith("/domain-access/workspaces") ? null : captureWorkspaceRequest(fetchOptions.signal);
  const headers = new Headers(fetchOptions.headers);

  // Add Content-Type for JSON if body is present and not FormData
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const method = (fetchOptions.method || "GET").toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  try {
    if (lifetime) {
      fetchOptions.signal = lifetime.signal;
      const scoped = payloadHeaders(endpoint, headers);
      scoped.forEach((value, key) => headers.set(key, value));
    }
    let res = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: "include",
    });

    lifetime?.assertCurrent();
    // 401 = session cookie missing/expired/invalidated — try a refresh once
    // (there's no client-visible way to know in advance whether a refresh
    // cookie exists, since it's HttpOnly; just attempt it). Skipped
    // entirely for authPolicy "none": a 401 from e.g. /auth/login is a
    // real "wrong password" response, not a session problem.
    if (res.status === 401 && authPolicy !== "none") {
      const refreshResult = await refreshToken();
      lifetime?.assertCurrent();

      if (refreshResult.ok) {
        const retryHeaders = new Headers(headers);
        if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
          retryHeaders.set("Content-Type", "application/json");
        }
        if (MUTATING_METHODS.has(method)) {
          const csrf = getCsrfToken();
          if (csrf) retryHeaders.set("X-CSRF-Token", csrf);
        }
        res = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...fetchOptions,
          headers: retryHeaders,
          credentials: "include",
        });
      } else if (authPolicy === "required") {
        await forceLoginRedirect();
        const message = refreshResult.accountInactive
          ? "Account is not active"
          : "Session expired";
        return { ok: false, status: 401, data: { message } as T };
      } else {
        // "optional" — quietly report the failure, no redirect. The caller
        // (e.g. the initial auth probe) treats this the same as "logged out".
        const data = await res.json().catch(() => ({}));
        lifetime?.assertCurrent();
        return { ok: false, status: 401, data: data as T };
      }
    }

    // ACCOUNT_INACTIVE (see AccountInactiveError / X-Account-Status) means
    // the account itself is banned/suspended — distinct from an ordinary
    // permission-403, which should NOT force a logout. Only "required"
    // calls act on this; "optional"/"none" just hand the response back.
    if (res.status === 403 && authPolicy === "required") {
      const data = await res.json().catch(() => ({}));
      lifetime?.assertCurrent();
      const inactive =
        (data as any)?.error?.code === "ACCOUNT_INACTIVE" ||
        res.headers.get("X-Account-Status") === "inactive";
      if (inactive) {
        await forceLoginRedirect();
        return { ok: false, status: 403, data: data as T };
      }
      return { ok: res.ok, status: res.status, data: data as T };
    }

    const data = await res.json().catch(() => ({}));
    lifetime?.assertCurrent();
    return { ok: res.ok, status: res.status, data: data as T };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Network error";
    return { ok: false, status: 0, data: { message } as T, error: message };
  } finally { lifetime?.release(); }
}

// ===== Analytics API =====
export async function getAnalytics() {
  return apiFetch<AnalyticsData>("/analytics/");
}

export function streamAnalytics(onData: (data: AnalyticsData) => void, onError?: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  // No more ?token= in the URL (Phase 2) — withCredentials makes the
  // browser attach the access_token cookie automatically, the same way any
  // other fetch on this app does.
  const url = new URL(`${API_BASE_URL}/analytics/stream`, window.location.origin);
  return workspaceStream(url.toString(), e => onData(JSON.parse(e.data)), onError);
}

// ===== Auth API =====
// All of these use authPolicy: "none" — a 401/403 here is a real answer
// from the endpoint itself (wrong password, expired/invalid code, banned
// account) that the caller (the login/register/etc. form) must display
// verbatim. None of them are "session expired" in disguise, so none of
// them should trigger a refresh attempt or a forced redirect.
export async function apiLogin(email: string, password: string) {
  return apiFetch<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    authPolicy: "none",
  });
}

export async function apiRegister(payload: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
}) {
  return apiFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
    authPolicy: "none",
  });
}

export async function apiVerifyOtp(email: string, code: string) {
  return apiFetch("/auth/register/verify-otp", {
    method: "POST",
    body: JSON.stringify({ email, code }),
    authPolicy: "none",
  });
}

export async function apiResendOtp(email: string) {
  return apiFetch("/auth/resend-otp", {
    method: "POST",
    body: JSON.stringify({ email }),
    authPolicy: "none",
  });
}

export async function apiVerifyTwoFactor(email: string, code: string, tempToken: string) {
  return apiFetch<LoginResponse>("/auth/verify-2fa", {
    method: "POST",
    body: JSON.stringify({ email, code, temp_token: tempToken }),
    authPolicy: "none",
  });
}

export async function apiForgotPassword(email: string) {
  return apiFetch("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
    authPolicy: "none",
  });
}

export async function apiResetPassword(email: string, code: string, newPassword: string) {
  return apiFetch("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ email, code, new_password: newPassword }),
    authPolicy: "none",
  });
}

export async function apiLogout() {
  // Explicit user logout revokes the session. Its response must never
  // enter automatic refresh or redirect handling.
  return apiFetch("/auth/logout", { method: "POST", authPolicy: "none" });
}

const sessionStatusSchema = z.object({
  session_id: z.string().uuid(), server_time: z.string().datetime({ offset: true }),
  idle_expires_at: z.string().datetime({ offset: true }), absolute_expires_at: z.string().datetime({ offset: true }),
});

async function sessionRequest(activity: boolean): Promise<SessionStatus> {
  const result = await apiFetch<unknown>(activity ? "/auth/session/activity" : "/auth/session", {
    method: activity ? "POST" : "GET", ...(activity ? { body: "{}" } : {}),
    cache: "no-store", authPolicy: "optional",
  });
  if (!result.ok) {
    if (result.status === 401) throw new SessionExpiredError("Session expired");
    throw new Error("Session verification is unavailable");
  }
  const parsed = sessionStatusSchema.safeParse(result.data);
  if (!parsed.success) throw new Error("Session verification is unavailable");
  return parsed.data;
}

export const getSessionStatus = () => sessionRequest(false);
export const recordSessionActivity = () => sessionRequest(true);

export async function apiGetProfile(authPolicy: AuthPolicy = "required") {
  return apiFetch<User>("/profile/me", { authPolicy });
}

// ===== Pipeline API =====
const intakeResponseSchema = z.object({ request_id: z.string().uuid(), replayed: z.boolean(), status: z.string(), message: z.string() });
export async function submitPipelineJob(formData: FormData, scope: WorkspaceIdentity) {
  const key=z.string().uuid().parse(formData.get("request_id"));
  const result=await apiFetch<unknown>("/pipeline/process", {
    method: "POST",
    body: formData,
    headers: workspaceHeaders(scope),
  });
  if(result.ok){
    const data=intakeResponseSchema.extend({pipeline_job_id:z.string().min(1)}).parse(result.data);
    if(data.request_id!==key)throw new Error("Submission identity does not match");
    return {...result,data};
  }
  return result;
}

export async function listPipelineRuns(skip: number, limit: number, scope: WorkspaceIdentity) {
  return apiFetch<{ items: PipelineRun[]; total: number }>(
    `/pipeline/runs?skip=${skip}&limit=${limit}`,
    { headers: workspaceHeaders(scope) }
  );
}

export async function getPipelineRunOutput(jobId: string) {
  return apiFetch<PipelineRun>(`/pipeline/runs/${jobId}/output`);
}

export async function generatePipelineWordExport(jobId: string) {
  const headers = new Headers();
  const csrf = getCsrfToken();
  if (csrf) headers.set("X-CSRF-Token", csrf);

  const res = await workspaceFetch(`${API_BASE_URL}/pipeline/runs/${jobId}/export/word`, {
    method: "POST",
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Failed to generate document: ${res.statusText}`);
  }

  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return res.json(); // Returns { id: string }
  }

  return res.blob();
}

export async function getPipelineWordExportContent(docId: string) {
  const res = await workspaceFetch(`${API_BASE_URL}/pipeline/runs/export/word/${docId}`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch document content: ${res.statusText}`);
  }

  const data = await res.json();
  if (!data.content) {
    throw new Error("Invalid response: No base64 content found");
  }

  // Convert base64 to blob
  const byteCharacters = atob(data.content);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  
  return new Blob(byteArrays, { type: data.content_type || "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
}

export async function listPipelineWordExports(jobId: string) {
  const res = await workspaceFetch(`${API_BASE_URL}/pipeline/runs/${jobId}/export/word`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`Failed to list documents: ${res.statusText}`);
  }

  return res.json();
}

export async function deletePipelineRun(jobId: string) {
  return apiFetch(`/pipeline/runs/${jobId}`, { method: "DELETE" });
}

export async function cancelPipelineRun(jobId: string) {
  return apiFetch(`/pipeline/runs/${jobId}/cancel`, { method: "POST" });
}

export async function submitPipelineRunFeedback(jobId: string, isHelpful: boolean, feedbackText?: string) {
  return apiFetch(`/pipeline/runs/${jobId}/feedback`, {
    method: "POST",
    body: JSON.stringify({ is_helpful: isHelpful, feedback_text: feedbackText }),
  });
}

export async function getPipelineJobSteps(jobId: string) {
  const endpoints = [
    `/pipeline/runs/${jobId}/steps`,
    `/pipeline/${jobId}/steps`,
  ];

  let lastResponse: ApiResponse<unknown> | null = null;
  let lastOkEmpty: ApiResponse<{ items: PipelineStep[] }> | null = null;

  for (const endpoint of endpoints) {
    const res = await apiFetch<unknown>(endpoint);
    lastResponse = res;
    if (res.ok) {
      const items = parsePipelineStepsResponse(res.data);
      if (items.length > 0) {
        return {
          ok: true,
          status: res.status,
          data: { items },
        };
      }
      lastOkEmpty = {
        ok: true,
        status: res.status,
        data: { items },
      };
    }
  }

  if (lastOkEmpty) return lastOkEmpty;

  return {
    ok: false,
    status: lastResponse?.status ?? 404,
    data: { items: [] as PipelineStep[] },
    error: lastResponse?.error ?? "Failed to load steps",
  };
}

export async function getPipelineStepDetail(jobId: string, stepId: string) {
  return apiFetch<PipelineStep>(`/pipeline/${jobId}/steps/${stepId}`);
}

export async function unsealAndDownloadZip(jobId: string) {
  return apiFetch(`/pipeline/runs/${jobId}/unseal`, { method: "POST" });
}

export async function getActiveRunSettings() {
  return apiFetch("/run-settings/active");
}

export async function updateActiveRunSettings(settings: Record<string, any>) {
  return apiFetch("/run-settings/active", {
    method: "PATCH",
    body: JSON.stringify(settings),
  });
}

export async function chatWithRun(jobId: string, message: string, requestId: string, files: File[] = []) {
  const formData = new FormData();
  formData.append("message", message);
  formData.append("request_id", requestId);
  files.forEach(f => formData.append("files", f));
  
  return apiFetch<{ task_id: string; message_id: string; status: string; replayed: boolean }>(`/pipeline/runs/${encodeURIComponent(jobId)}/chat`, {
    method: "POST",
    body: formData,
  });
}

export async function getRunChatHistory(jobId: string) {
  return apiFetch<any[]>(`/pipeline/runs/${jobId}/chat/history`);
}

export async function evaluateChatMessage(jobId: string, messageId: string, isHelpful: boolean, feedback?: string) {
  return apiFetch(`/pipeline/runs/${jobId}/chat/evaluate`, {
    method: "POST",
    body: JSON.stringify({ message_id: messageId, is_helpful: isHelpful, feedback }),
  });
}

export async function exploreVault(jobId: string, skip = 0, limit = 10, secret?: string) {
  const formData = new FormData();
  if (secret) formData.append("vault_secret", secret);
  
  return apiFetch<{ files: any[]; total: number }>(`/pipeline/runs/${jobId}/explore?skip=${skip}&limit=${limit}`, {
    method: "POST",
    body: formData,
  });
}

// ===== Profile API =====
export async function updateProfile(data: Partial<User>) {
  return apiFetch("/profile/me/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
}) {
  // Backend expects `old_password`, not `current_password`.
  return apiFetch("/profile/me/change-password", {
    method: "POST",
    body: JSON.stringify({
      old_password: data.current_password,
      new_password: data.new_password,
    }),
  });
}

export async function requestEmailChange(data: { new_email: string; password: string }) {
  return apiFetch("/profile/me/change-email", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function verifyEmailChange(data: { code: string }) {
  return apiFetch<User>("/profile/me/verify-email-change", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function requestPhoneVerification() {
  return apiFetch("/profile/me/verify-phone", { method: "POST" });
}

export async function confirmPhoneVerification(data: { code: string }) {
  return apiFetch<User>("/profile/me/verify-phone/confirm", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteAccount(data: { password: string; confirmation: string }) {
  return apiFetch("/profile/me/account", {
    method: "DELETE",
    body: JSON.stringify(data),
  });
}

export async function enable2FA(data: { password: string }) {
  return apiFetch("/profile/me/2fa/enable", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function disable2FA(data: { password: string }) {
  return apiFetch("/profile/me/2fa/disable", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getSessions() {
  return apiFetch<Session[]>("/profile/me/sessions");
}

export async function revokeSession(sessionId: string) {
  return apiFetch(`/profile/me/sessions/${sessionId}`, { method: "DELETE" });
}


// ===== Logs API =====
export async function getActivityLogs(skip = 0, limit = 50) {
  return apiFetch<ActivityLog[]>(`/logs/activity?skip=${skip}&limit=${limit}`);
}

export async function getApiRequestLogs(skip = 0, limit = 50) {
  return apiFetch<ApiRequestLog[]>(`/logs/api-requests?skip=${skip}&limit=${limit}`);
}

function streamLogs<T>(
  streamPath: string,
  onData: (items: T[]) => void,
  onError?: () => void
): () => void {
  if (typeof window === "undefined") return () => {};
  // No more ?token= (Phase 2) — see streamAnalytics above.
  const url = new URL(`${API_BASE_URL}${streamPath}`, window.location.origin);
  return workspaceStream(url.toString(), (e) => {
    try {
      const parsed = JSON.parse(e.data);
      if (parsed?.error) {
        onError?.();
        return;
      }
      onData(Array.isArray(parsed) ? parsed : parsed?.items ?? []);
    } catch {
      /* ignore malformed frames */
    }
  }, onError);
}

export function streamActivityLogs(
  onData: (items: ActivityLog[]) => void,
  onError?: () => void
): () => void {
  return streamLogs<ActivityLog>("/logs/activity/stream", onData, onError);
}

export function streamApiRequestLogs(
  onData: (items: ApiRequestLog[]) => void,
  onError?: () => void
): () => void {
  return streamLogs<ApiRequestLog>("/logs/api-requests/stream", onData, onError);
}

// ===== API Keys API =====
export async function getApiKeys() {
  return apiFetch<{ keys: ApiKey[] }>("/api-keys");
}

export async function createApiKey(name: string) {
  return apiFetch<{ key: string; api_key: ApiKey }>("/api-keys", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function revokeApiKey(keyId: string) {
  return apiFetch(`/api-keys/${keyId}`, { method: "DELETE" });
}

// ===== Prompts API =====
export type ArtifactScope = "own" | "global";

function artifactPath(name: string) {
  return name.split("/").map(encodeURIComponent).join("/");
}

function scopeQuery(scope?: ArtifactScope) {
  return scope ? `?scope=${scope}` : "";
}

export async function listPrompts(params: { is_active?: boolean; limit?: number; skip?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  return apiFetch<{ items: any[]; total: number }>(`/prompts?${qs.toString()}`);
}

export async function createPrompt(data: Record<string, unknown>) {
  return apiFetch("/prompts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePrompt(name: string, data: Record<string, unknown>) {
  return apiFetch(`/prompts/${artifactPath(name)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deletePrompt(name: string, scope?: ArtifactScope) {
  return apiFetch(`/prompts/${artifactPath(name)}${scopeQuery(scope)}`, { method: "DELETE" });
}

export async function listPromptVersions(name: string, type?: string, scope?: ArtifactScope) {
  const qs = new URLSearchParams();
  if (type) qs.set("type", type);
  if (scope) qs.set("scope", scope);
  const suffix = qs.size ? `?${qs.toString()}` : "";
  return apiFetch<any[]>(`/prompts/${artifactPath(name)}/versions${suffix}`);
}

export async function deletePromptVersion(name: string, version: number, scope?: ArtifactScope) {
  return apiFetch(`/prompts/${artifactPath(name)}/versions/${version}${scopeQuery(scope)}`, { method: "DELETE" });
}

export async function activatePromptVersion(name: string, version: number, scope?: ArtifactScope) {
  return apiFetch(`/prompts/${artifactPath(name)}/versions/${version}/activate${scopeQuery(scope)}`, { method: "POST" });
}

export interface PromptKnowledgeSource {
  source_id: string;
  domain_id: string;
  version: number;
  content_digest: string;
  source_type: string;
  name: string;
  excerpt: string;
}

export interface PromptGenerateResponse {
  suggested_name: string;
  system_content: string;
  user_content: string;
  reasoning: string;
  sources_used: PromptKnowledgeSource[];
}

export async function generatePrompt(data: {
  domain_id: string;
  description: string;
  mode?: "create" | "edit";
  suggested_name?: string;
  domain?: string;
  current_system_content?: string;
  current_user_content?: string;
  top_k?: number;
}) {
  return apiFetch<PromptGenerateResponse>("/prompts/generate", {
    method: "POST", cache: "no-store",
    body: JSON.stringify({ ...data, domain_id: z.string().uuid().parse(data.domain_id) }),
  });
}

export async function reindexPromptKnowledge(domainId: string, kind: "prompt" | "config" = "prompt", operationBudgetId?: string, clientRequestId: string = crypto.randomUUID()) {
  const qs = new URLSearchParams({ domain_id: z.string().uuid().parse(domainId), kind: z.enum(["prompt", "config"]).parse(kind) });
  qs.set("client_request_id", z.string().uuid().refine(id => id !== "00000000-0000-0000-0000-000000000000").parse(clientRequestId));
  if (operationBudgetId) qs.set("operation_budget_id", z.string().uuid().parse(operationBudgetId));
  return apiFetch<{ status: string; domain_id: string; kind: "prompt" | "config"; task_id: string; task_status: string }>(
    `/prompts/knowledge/reindex?${qs}`, { method: "POST", cache: "no-store" }
  );
}

// ===== Prompts File System API =====
export interface FSTreeNode {
  name: string;
  path: string;
  type: "folder" | "file";
  children?: FSTreeNode[];
}

export interface FSPromptResponse {
  path: string;
  system_content: string;
  user_content: string;
  metadata_config?: Record<string, any>;
  system_version?: number;
  user_version?: number;
}

export interface PromptVersionItem {
  id: number;
  version: number;
  type: string;
  content: string;
  is_active: boolean;
  updated_at: string;
  edited_by: string;
}

export async function listPromptsFsTree() {
  return apiFetch<FSTreeNode[]>("/prompts-fs/directory/tree");
}

export async function createFolderFs(parentPath: string, name: string) {
  return apiFetch("/prompts-fs/directory/folder", {
    method: "POST",
    body: JSON.stringify({ parent_path: parentPath, name }),
  });
}

export async function createPromptFs(data: {
  path: string;
  system_content: string;
  user_content: string;
  metadata_config?: Record<string, any>;
}) {
  return apiFetch<FSPromptResponse>("/prompts-fs/directory/prompt", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getPromptFs(path: string) {
  const qs = new URLSearchParams({ path });
  return apiFetch<FSPromptResponse>(`/prompts-fs/directory/prompt?${qs.toString()}`);
}

export async function updatePromptFs(data: {
  path: string;
  system_content: string;
  user_content: string;
  metadata_config?: Record<string, any>;
}) {
  return apiFetch<FSPromptResponse>("/prompts-fs/directory/prompt", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deletePromptFs(path: string) {
  const qs = new URLSearchParams({ path });
  return apiFetch(`/prompts-fs/directory/prompt?${qs.toString()}`, { method: "DELETE" });
}

export async function deleteFolderFs(path: string) {
  const qs = new URLSearchParams({ path });
  return apiFetch(`/prompts-fs/directory/folder?${qs.toString()}`, { method: "DELETE" });
}

export async function listPromptVersionsFs(path: string, promptType: "system" | "user") {
  const qs = new URLSearchParams({ path, prompt_type: promptType });
  return apiFetch<PromptVersionItem[]>(`/prompts-fs/directory/prompt/versions?${qs.toString()}`);
}

// ===== Configs API =====
export async function getConfigs(params: { is_active?: boolean; limit?: number; skip?: number; name?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.name !== undefined) qs.set("name", params.name);
  return apiFetch<{ items: any[]; total: number }>(`/configs?${qs.toString()}`);
}

export async function createConfig(data: Record<string, unknown>) {
  return apiFetch("/configs", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateConfig(name: string, data: Record<string, unknown>) {
  return apiFetch(`/configs/${artifactPath(name)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteConfig(name: string, scope?: ArtifactScope) {
  return apiFetch(`/configs/${artifactPath(name)}${scopeQuery(scope)}`, { method: "DELETE" });
}

export async function listConfigVersions(name: string, scope?: ArtifactScope) {
  return apiFetch<any[]>(`/configs/${artifactPath(name)}/versions${scopeQuery(scope)}`);
}

export async function deleteConfigVersion(name: string, version: number, scope?: ArtifactScope) {
  return apiFetch(`/configs/${artifactPath(name)}/versions/${version}${scopeQuery(scope)}`, { method: "DELETE" });
}

export async function activateConfigVersion(name: string, version: number, scope?: ArtifactScope) {
  return apiFetch(`/configs/${artifactPath(name)}/versions/${version}/activate${scopeQuery(scope)}`, { method: "POST" });
}

export interface ConfigSourceCitation {
  source_id: string;
  domain_id: string;
  version: number;
  content_digest: string;
  name: string;
  excerpt: string;
}

export interface ConfigGenerateResponse {
  suggested_name: string;
  config: Record<string, any> | any[];
  reasoning: string;
  sources_used: ConfigSourceCitation[];
}

export async function generateConfig(data: {
  domain_id: string;
  description: string;
  suggested_name?: string;
  top_k?: number;
}) {
  return apiFetch<ConfigGenerateResponse>("/configs/generate", {
    method: "POST", cache: "no-store",
    body: JSON.stringify({ ...data, domain_id: z.string().uuid().parse(data.domain_id) }),
  });
}

export async function listWorkflowLookups(limit = 100) {
  return apiFetch<any[]>(`/workflow-lookup/?limit=${limit}`);
}

export async function createWorkflowLookup(data: Record<string, unknown>) {
  return apiFetch("/workflow-lookup/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateWorkflowLookup(id: string, data: Record<string, unknown>) {
  return apiFetch(`/workflow-lookup/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteWorkflowLookup(id: string) {
  return apiFetch(`/workflow-lookup/${id}`, { method: "DELETE" });
}

// ===== Domains API =====
export async function listDomains(params: { is_active?: boolean; name?: string; skip?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params.name !== undefined) qs.set("name", params.name);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: Domain[]; total: number; skip: number; limit: number }>(`/domains?${qs.toString()}`);
}

export async function getDomain(domainId: string) {
  return apiFetch<Domain>(`/domains/${domainId}`);
}

export async function createDomain(data: {
  domain_name: string;
  domain?: Record<string, any>;
  prompt_ids?: number[];
  config_ids?: number[];
  user_id?: string;
  sub_domains?: SubDomainNested[];
}) {
  return apiFetch<Domain>("/domains", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateDomain(
  domainId: string,
  data: {
    domain_name?: string;
    domain?: Record<string, any>;
    prompt_ids?: number[];
    config_ids?: number[];
    is_active?: boolean;
    sub_domains?: SubDomainNested[];
  }
) {
  return apiFetch<Domain>(`/domains/${domainId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteDomain(domainId: string) {
  return apiFetch<{ message: string }>(`/domains/${domainId}`, { method: "DELETE" });
}

export async function activateDomain(domainId: string) {
  return apiFetch<{ message: string }>(`/domains/${domainId}/activate`, { method: "POST" });
}

export async function deactivateDomain(domainId: string) {
  return apiFetch<{ message: string }>(`/domains/${domainId}/deactivate`, { method: "POST" });
}

// ===== Main Domains API =====
export async function listMainDomains(params: { name?: string; skip?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.name !== undefined) qs.set("name", params.name);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: MainDomain[]; total: number; skip: number; limit: number }>(`/main-domains?${qs.toString()}`);
}

export async function getMainDomain(id: string) {
  return apiFetch<MainDomain>(`/main-domains/${id}`);
}

export async function createMainDomain(data: {
  name: string;
  domain_id?: string | null;
  sub_domain_id?: string | null;
}) {
  return apiFetch<MainDomain>("/main-domains", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateMainDomain(
  id: string,
  data: {
    name?: string;
    domain_id?: string | null;
    sub_domain_id?: string | null;
  }
) {
  return apiFetch<MainDomain>(`/main-domains/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteMainDomain(id: string, soft = true) {
  return apiFetch<{ message: string }>(`/main-domains/${id}?soft=${soft}`, { method: "DELETE" });
}


// ===== AI Models API =====
export interface AIModel {
  id?: string;
  uuid?: string;
  model_name: string;
  use_model: boolean;
  model_base_url?: string;
  has_credentials: boolean;
  credential_state: "vault" | "none" | "unconfigured" | "legacy";
  credential_revision: number;
  is_active: boolean;
  input_token_price?: number;
  output_token_price?: number;
  created_at?: string;
  updated_at?: string;
}

export type AIModelWrite = Partial<Pick<AIModel, "model_name" | "model_base_url" | "use_model" | "is_active" | "input_token_price" | "output_token_price">> & {
  model_api_key?: string;
  credential_mode?: "vault" | "none";
  expected_credential_revision?: number;
};

const AIModelResponseSchema = z.object({
  id: z.string().uuid(), model_name: z.string(), use_model: z.boolean(), is_active: z.boolean(),
  model_base_url: z.string().nullish().transform(value => value ?? undefined),
  has_credentials: z.boolean(), credential_state: z.enum(["vault", "none", "unconfigured", "legacy"]),
  credential_revision: z.number().int().nonnegative(),
  input_token_price: z.number().optional(), output_token_price: z.number().optional(),
  created_at: z.string().optional(), updated_at: z.string().optional(),
});
const AIModelListSchema = z.object({ items: z.array(AIModelResponseSchema), total: z.number().int().nonnegative(),
  skip: z.number().int().nonnegative(), limit: z.number().int().positive() });

function checkedModelResponse<T>(response: ApiResponse<T>, schema: z.ZodTypeAny): ApiResponse<T> {
  if (!response.ok) return response;
  const parsed = schema.safeParse(response.data);
  if (!parsed.success) throw new Error("Invalid model response");
  return { ...response, data: parsed.data };
}

export async function listAIModels() {
  const response = await apiFetch<{ items: AIModel[]; total: number; skip: number; limit: number }>("/ai-models", { cache: "no-store" });
  return checkedModelResponse(response, AIModelListSchema);
}

export async function createAIModel(data: AIModelWrite) {
  const response = await apiFetch<AIModel>("/ai-models", {
    method: "POST", body: JSON.stringify(data), cache: "no-store",
  });
  return checkedModelResponse(response, AIModelResponseSchema);
}

export async function updateAIModel(modelId: string, data: AIModelWrite) {
  const response = await apiFetch<AIModel>(`/ai-models/${z.string().uuid().parse(modelId)}`, {
    method: "PATCH", body: JSON.stringify(data), cache: "no-store",
  });
  return checkedModelResponse(response, AIModelResponseSchema);
}

export async function deleteAIModel(modelId: string) {
  return apiFetch<{ message: string }>(`/ai-models/${modelId}`, { method: "DELETE" });
}


// ===== Agents API =====
export interface Agent {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listAgents(params: { is_active?: boolean; name?: string; skip?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.is_active !== undefined) qs.set("is_active", String(params.is_active));
  if (params.name !== undefined) qs.set("name", params.name);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: Agent[]; total: number; skip: number; limit: number }>(`/agents?${qs.toString()}`);
}

export async function createAgent(data: { name: string; description?: string; is_active?: boolean }) {
  return apiFetch<Agent>("/agents", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAgent(agentId: string, data: { name?: string; description?: string; is_active?: boolean }) {
  return apiFetch<Agent>(`/agents/${agentId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAgent(agentId: string) {
  return apiFetch<{ message: string }>(`/agents/${agentId}`, { method: "DELETE" });
}

// ===== Document Keywords API =====
export interface DocumentKeyword {
  id: string;
  document_type: string;
  keyword: string;
  agent_id: string;
  domain_id?: string;
  subdomain_id?: string;
  created_at: string;
  updated_at: string;
}

export async function listDocumentKeywords(params: { agent_id?: string; document_type?: string; domain_id?: string; subdomain_id?: string; keyword?: string; skip?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.agent_id !== undefined) qs.set("agent_id", params.agent_id);
  if (params.document_type !== undefined) qs.set("document_type", params.document_type);
  if (params.domain_id !== undefined) qs.set("domain_id", params.domain_id);
  if (params.subdomain_id !== undefined) qs.set("subdomain_id", params.subdomain_id);
  if (params.keyword !== undefined) qs.set("keyword", params.keyword);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: DocumentKeyword[]; total: number; skip: number; limit: number }>(`/document-keywords?${qs.toString()}`);
}

export async function createDocumentKeyword(data: { document_type: string; keyword: string; agent_id: string; domain_id?: string; subdomain_id?: string }) {
  return apiFetch<DocumentKeyword>("/document-keywords", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateDocumentKeyword(keywordId: string, data: { document_type?: string; keyword?: string; agent_id?: string; domain_id?: string; subdomain_id?: string }) {
  return apiFetch<DocumentKeyword>(`/document-keywords/${keywordId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteDocumentKeyword(keywordId: string) {
  return apiFetch<{ message: string }>(`/document-keywords/${keywordId}`, { method: "DELETE" });
}

// ===== Agent Model Pool API =====
export interface AgentModelPool {
  id: string;
  agent_id: string;
  agent_name?: string;
  domain_id: string;
  subdomain_id: string;
  subdomain_title?: string;
  model_id: string;
  model_name?: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listAgentModelPools(params: {
  agent_id?: string;
  domain_id?: string;
  subdomain_id?: string;
  active_only?: boolean;
  skip?: number;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.agent_id !== undefined) qs.set("agent_id", params.agent_id);
  if (params.domain_id !== undefined) qs.set("domain_id", params.domain_id);
  if (params.subdomain_id !== undefined) qs.set("subdomain_id", params.subdomain_id);
  if (params.active_only !== undefined) qs.set("active_only", String(params.active_only));
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: AgentModelPool[]; total: number; skip: number; limit: number }>(`/agent-model-pool?${qs.toString()}`);
}

export async function createAgentModelPool(data: {
  agent_id: string;
  domain_id: string;
  subdomain_id: string;
  model_id: string;
  priority?: number;
}) {
  return apiFetch<AgentModelPool>("/agent-model-pool", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAgentModelPool(
  poolId: string,
  data: {
    priority?: number;
    is_active?: boolean;
  }
) {
  return apiFetch<AgentModelPool>(`/agent-model-pool/${poolId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAgentModelPool(poolId: string) {
  return apiFetch<{ message: string }>(`/agent-model-pool/${poolId}`, { method: "DELETE" });
}


// ===== Worker Administration API =====
export interface WorkerPoolSummary {
  pool_name: string;
  desired_processes: number;
  minimum_processes: number;
  maximum_processes: number;
  enabled: boolean;
  version: number;
  actual_processes: number;
  starting_processes: number;
  idle_processes: number;
  busy_processes: number;
  draining_processes: number;
  pending_tasks: number;
  failed_tasks: number;
  oldest_pending_at: string | null;
  updated_at: string;
}

export interface WorkerSupervisorStatus {
  healthy: boolean;
  owner_id: string | null;
  hostname: string | null;
  pid: number | null;
  status: string;
  lease_expires_at: string | null;
  last_heartbeat_at: string | null;
  started_at: string | null;
}

export interface WorkerInstance {
  id: string;
  supervisor_id: string;
  pool_name: string;
  hostname: string;
  pid: number;
  status: string;
  drain_requested: boolean;
  current_work_id: string | null;
  tasks_completed: number;
  last_error: string | null;
  started_at: string;
  last_heartbeat_at: string;
  stopped_at: string | null;
}

export async function listWorkerPools() {
  return apiFetch<WorkerPoolSummary[]>("/admin/workers/pools");
}

export async function getWorkerSupervisor() {
  return apiFetch<WorkerSupervisorStatus>("/admin/workers/supervisor");
}

export async function listWorkerInstances(poolName?: string) {
  const query = poolName ? `?pool_name=${encodeURIComponent(poolName)}` : "";
  return apiFetch<WorkerInstance[]>(`/admin/workers/instances${query}`);
}

export async function scaleWorkerPool(pool: WorkerPoolSummary, delta: number) {
  return apiFetch<WorkerPoolSummary>(`/admin/workers/pools/${pool.pool_name}/scale`, {
    method: "POST",
    body: JSON.stringify({ delta, expected_version: pool.version }),
  });
}

export async function setWorkerPoolSize(pool: WorkerPoolSummary, desired: number) {
  return apiFetch<WorkerPoolSummary>(`/admin/workers/pools/${pool.pool_name}`, {
    method: "PATCH",
    body: JSON.stringify({ desired_processes: desired, expected_version: pool.version }),
  });
}

export async function drainWorkerPool(poolName: string) {
  return apiFetch<{ pool_name: string; instances_requested: number }>(
    `/admin/workers/pools/${poolName}/drain`,
    { method: "POST" },
  );
}

// ===== Admin user-management API (Phase 5 of [[rbac-permission-overhaul-initiative]]) =====
// Every one of these hits a route gated on a users.* permission that is
// NOT API-key-eligible — irrelevant here since the browser always
// authenticates via the session cookie, but worth knowing if this file is
// ever reused outside the browser context.

export type AccountStatus = "active" | "suspended" | "banned";
export type PermissionEffect = "allow" | "deny";
export type PermissionSource = "role" | "allow_override" | "deny_override" | "none";

export interface UserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role_id: number;
  role_name: string;
  account_status: AccountStatus;
  is_active: boolean;
  email_verified: boolean;
  last_login_at?: string | null;
  created_at: string;
}

// ===== Role administration (dynamic, admin-manageable roles — replaces
// hand-editing individual users' permission overrides one at a time) =====

export interface RoleSummary {
  id: number;
  name: string;
  description?: string | null;
  is_system: boolean;
  user_count: number;
}

export interface RolePermissionEntry {
  key: string;
  module: string;
  description: string;
  is_sensitive: boolean;
  is_api_key_eligible: boolean;
  granted: boolean;
}

export interface RoleDetail extends RoleSummary {
  permissions: RolePermissionEntry[];
}

export async function listRoles() {
  return apiFetch<RoleSummary[]>("/roles");
}

export async function getRoleDetail(roleId: number) {
  return apiFetch<RoleDetail>(`/roles/${roleId}`);
}

export async function createRole(data: { name: string; description?: string | null }) {
  return apiFetch<RoleDetail>("/roles", { method: "POST", body: JSON.stringify(data) });
}

export async function updateRole(
  roleId: number,
  data: { name?: string | null; description?: string | null }
) {
  return apiFetch<RoleDetail>(`/roles/${roleId}`, { method: "PATCH", body: JSON.stringify(data) });
}

export async function deleteRole(roleId: number) {
  return apiFetch<void>(`/roles/${roleId}`, { method: "DELETE" });
}

export async function grantRolePermission(roleId: number, permissionKey: string) {
  return apiFetch<RolePermissionEntry>(`/roles/${roleId}/permissions`, {
    method: "POST",
    body: JSON.stringify({ permission_key: permissionKey }),
  });
}

export async function revokeRolePermission(roleId: number, permissionKey: string) {
  return apiFetch<{ permission_key: string; removed: boolean }>(
    `/roles/${roleId}/permissions/${encodeURIComponent(permissionKey)}`,
    { method: "DELETE" }
  );
}

export interface EffectivePermission {
  key: string;
  module: string;
  description: string;
  is_sensitive: boolean;
  is_api_key_eligible: boolean;
  granted: boolean;
  source: PermissionSource;
  override_reason?: string | null;
  override_expires_at?: string | null;
  override_granted_by?: string | null;
}

export interface UserDetail extends UserSummary {
  phone_number: string;
  phone_verified: boolean;
  two_factor_enabled: boolean;
  status_reason?: string | null;
  status_changed_by?: string | null;
  status_changed_at?: string | null;
  suspended_until?: string | null;
  permissions: EffectivePermission[];
}

export interface PermissionCatalogEntry {
  key: string;
  module: string;
  description: string;
  is_sensitive: boolean;
  is_api_key_eligible: boolean;
}

export interface UserAuditLogEntry {
  id: string;
  action: string;
  resource?: string | null;
  details?: string | null;
  status: string;
  ip_address?: string | null;
  actor_user_id?: string | null;
  target_user_id?: string | null;
  created_at: string;
}

export async function listUsers(params: {
  skip?: number;
  limit?: number;
  search?: string;
  role_id?: number;
  account_status?: AccountStatus;
} = {}) {
  const qs = new URLSearchParams();
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.role_id !== undefined) qs.set("role_id", String(params.role_id));
  if (params.account_status) qs.set("account_status", params.account_status);
  return apiFetch<{ items: UserSummary[]; total: number; skip: number; limit: number }>(
    `/users?${qs.toString()}`
  );
}

export async function getUserDetail(userId: string) {
  return apiFetch<UserDetail>(`/users/${userId}`);
}

export async function updateUserStatus(
  userId: string,
  data: { status: AccountStatus; reason?: string; suspended_until?: string | null }
) {
  return apiFetch<UserDetail>(`/users/${userId}/status`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function updateUserRole(userId: string, roleId: number) {
  return apiFetch<UserDetail>(`/users/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role_id: roleId }),
  });
}

export async function revokeUserSessions(userId: string) {
  return apiFetch<UserDetail>(`/users/${userId}/revoke-sessions`, { method: "POST" });
}

export async function grantUserPermissionOverride(
  userId: string,
  data: { permission_key: string; effect: PermissionEffect; reason?: string; expires_at?: string | null }
) {
  return apiFetch(`/users/${userId}/permissions`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function resetUserPermissionOverride(userId: string, permissionKey: string) {
  return apiFetch<{ permission_key: string; removed: boolean }>(
    `/users/${userId}/permissions/${encodeURIComponent(permissionKey)}`,
    { method: "DELETE" }
  );
}

export async function getUserAuditLog(userId: string, skip = 0, limit = 50) {
  return apiFetch<UserAuditLogEntry[]>(`/users/${userId}/audit?skip=${skip}&limit=${limit}`);
}

export async function getPermissionCatalog() {
  return apiFetch<PermissionCatalogEntry[]>("/permissions/catalog");
}

// ===== Knowledge Base API =====
// Document-based knowledge base: upload a regulation/standard doc, an
// extraction pipeline distills it into compliance rules (status starts at
// "processing"), an admin reviews/edits the extracted rules once status
// reaches "pending_review", then activates or rejects the document. Only
// "active" documents feed into ComplianceAgent.select_rules() at evaluation
// time — see compliance_agent.py on the backend. Gated on a single
// "knowledge_base.manage" capability (no separate .view permission).
export type KnowledgeBaseStatus = "processing" | "pending_review" | "active" | "rejected" | "failed";

export interface KnowledgeBaseRule {
  id: string;
  rule_id: string;
  name: string;
  category?: string | null;
  severity?: string | null;
  description?: string | null;
  logic_hints?: string | null;
  parameters?: Record<string, any> | null;
}

export interface KnowledgeBaseDocument {
  id: string;
  domain_id: string;
  domain_title?: string | null;
  subdomain_id?: string | null;
  subdomain_title?: string | null;
  job_id?: string | null;
  title: string;
  status: KnowledgeBaseStatus;
  error_message?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  updated_at: string;
  rules: KnowledgeBaseRule[];
}

export interface KnowledgeBaseRuleUpdate {
  name?: string;
  category?: string;
  severity?: string;
  description?: string;
  logic_hints?: string;
  parameters?: Record<string, any>;
}

export interface KnowledgeBaseUploadResponse {
  request_id: string;
  replayed: boolean;
  knowledge_base_document_id: string;
  job_id: string;
  status: string;
  message: string;
}

export async function uploadKnowledgeBaseDocument(data: {
  file: File;
  request_id: string;
  tenant_id: string;
  domain_id: string;
  operation_budget_id: string;
  subdomain_id?: string;
  title?: string;
}) {
  const formData = new FormData();
  formData.append("file", data.file);
  formData.append("domain_id", data.domain_id);
  formData.append("operation_budget_id", data.operation_budget_id);
  formData.append("request_id", z.string().uuid().parse(data.request_id));
  if (data.subdomain_id) formData.append("subdomain_id", data.subdomain_id);
  if (data.title) formData.append("title", data.title);
  const result=await apiFetch<KnowledgeBaseUploadResponse>("/knowledge-base", {
    headers: { "X-Tenant-ID": data.tenant_id, "X-Domain-ID": data.domain_id },
    method: "POST",
    body: formData,
  });
  if(result.ok){
    const parsed=intakeResponseSchema.extend({knowledge_base_document_id:z.string().uuid(),job_id:z.string().min(1)}).parse(result.data);
    if(parsed.request_id!==data.request_id)throw new Error("Submission identity does not match");
    return {...result,data:parsed};
  }
  return result;
}

export async function listKnowledgeBaseDocuments(params: {
  domain_id?: string;
  subdomain_id?: string;
  status?: KnowledgeBaseStatus;
  skip?: number;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.domain_id !== undefined) qs.set("domain_id", params.domain_id);
  if (params.subdomain_id !== undefined) qs.set("subdomain_id", params.subdomain_id);
  if (params.status !== undefined) qs.set("status", params.status);
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  return apiFetch<{ items: KnowledgeBaseDocument[]; total: number; skip: number; limit: number }>(
    `/knowledge-base?${qs.toString()}`
  );
}

export async function getKnowledgeBaseDocument(documentId: string) {
  return apiFetch<KnowledgeBaseDocument>(`/knowledge-base/${documentId}`);
}

export async function updateKnowledgeBaseRule(
  documentId: string,
  ruleId: string,
  data: KnowledgeBaseRuleUpdate
) {
  return apiFetch<KnowledgeBaseRule>(`/knowledge-base/${documentId}/rules/${ruleId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function activateKnowledgeBaseDocument(documentId: string) {
  return apiFetch<KnowledgeBaseDocument>(`/knowledge-base/${documentId}/activate`, { method: "POST" });
}

export async function rejectKnowledgeBaseDocument(documentId: string) {
  return apiFetch<KnowledgeBaseDocument>(`/knowledge-base/${documentId}/reject`, { method: "POST" });
}

export async function deleteKnowledgeBaseDocument(documentId: string) {
  return apiFetch<void>(`/knowledge-base/${documentId}`, { method: "DELETE" });
}


// Institution configuration contains no credential or provider token.
export interface IdentityConfiguration { enterprise:boolean; local_credentials:boolean; connection_required:boolean; mfa_required:boolean; }
const identityConfigurationSchema=z.object({enterprise:z.boolean(),local_credentials:z.boolean(),connection_required:z.boolean(),mfa_required:z.boolean()}).strict();
export async function getIdentityConfiguration():Promise<IdentityConfiguration>{
  const response=await apiFetch<unknown>("/auth/enterprise/configuration",{authPolicy:"none",cache:"no-store",signal:AbortSignal.timeout(10000)});
  if(!response.ok)throw new Error("Institutional sign-in is unavailable");
  return identityConfigurationSchema.parse(response.data);
}
export const enterpriseSelectionSchema=z.object({tenant_id:z.string().uuid(),connection_id:z.string().uuid()}).strict();
export type EnterpriseSelection=z.infer<typeof enterpriseSelectionSchema>;
export function enterpriseSelection(search:string):EnterpriseSelection|null {
  const params=new URLSearchParams(search);
  if(!params.has("tenant_id")&&!params.has("connection_id"))return null;
  return enterpriseSelectionSchema.parse({tenant_id:params.get("tenant_id"),connection_id:params.get("connection_id")});
}
export function enterpriseLoginUrl(locale:string,selection:EnterpriseSelection|null=null):string{
  const query=new URLSearchParams({portal:"admin",locale:locale==="ar"?"ar":"en"});
  if(selection){const value=enterpriseSelectionSchema.parse(selection);query.set("tenant_id",value.tenant_id);query.set("connection_id",value.connection_id);}
  return `${API_BASE_URL}/auth/enterprise/start?${query}`;
}
const enterpriseAssuranceSchema=z.object({managed:z.boolean(),assurance:z.object({protocol:z.literal("oidc"),identity_id:z.string().uuid(),connection_id:z.string().uuid().nullable(),tenant_id:z.string().uuid().nullable(),connection_revision:z.number().int().positive().nullable(),portal:z.enum(["admin","user"]).nullable(),recovery_only:z.boolean(),level:z.enum(["mfa","single_factor"]),acr:z.string(),amr:z.array(z.string()),authenticated_at:z.string(),policy_revision:z.string()}).strict().nullable()}).strict();
export type EnterpriseAssurance=z.infer<typeof enterpriseAssuranceSchema>;
export async function getEnterpriseAssurance():Promise<EnterpriseAssurance>{
  const response=await apiFetch<unknown>("/auth/enterprise/session",{authPolicy:"optional",cache:"no-store",signal:AbortSignal.timeout(10000)});
  if(!response.ok)throw new Error("Authentication assurance is unavailable");
  return enterpriseAssuranceSchema.parse(response.data);
}
